// Tree
// Features:
// - Instantiated leaves for performance
// - Wind animation (vertex-like animation via CPU instancing)

import { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'

import leavesData from '../../instanceData/leaves.json'

const WIND_CONFIG = {
    strength: 0.6,
    frequency: 0.8,
    stretch: 0.08, // 8% stretch at peak
    rotate: 0.12, // ~10.3° max tilt on X
}

export default function Tree({
    position = [0, 0, 0],
    rotation = [0, 0, 0],
    scale = 1,
    material,
    nodes,
}) {
    const leavesRef = useRef()
    const count = leavesData.length

    // Precompute leaf transforms
    const base = useMemo(() => {
        const bPos = leavesData.map((l) => new THREE.Vector3(...l.position))
        const bRot = leavesData.map(
            (l) =>
                new THREE.Euler(
                    l.rotation[0],
                    l.rotation[1],
                    l.rotation[2],
                    'XYZ'
                )
        )
        const bScale = leavesData.map((l) => l.scale)
        const phase = leavesData.map(() => Math.random() * Math.PI * 2)

        return { bPos, bRot, bScale, phase }
    }, [])

    // Initial layout
    useEffect(() => {
        if (!leavesRef.current) return

        const dummy = new THREE.Object3D()
        for (let i = 0; i < count; i++) {
            dummy.position.copy(base.bPos[i])
            dummy.rotation.copy(base.bRot[i])
            const s = base.bScale[i]
            dummy.scale.set(s, s, s)
            dummy.updateMatrix()
            leavesRef.current.setMatrixAt(i, dummy.matrix)
        }
        leavesRef.current.instanceMatrix.needsUpdate = true
    }, [count, base])

    // Wind Animation Loop
    useFrame((state) => {
        if (!leavesRef.current) return

        const t = state.clock.elapsedTime
        const dummy = new THREE.Object3D()
        const omega = WIND_CONFIG.frequency * Math.PI * 2

        for (let i = 0; i < count; i++) {
            const wobble =
                Math.sin(omega * t + base.phase[i]) * WIND_CONFIG.strength
            const lift = Math.max(0, wobble)

            // Compose animated transform
            dummy.position.copy(base.bPos[i])

            dummy.rotation.set(
                base.bRot[i].x + wobble * WIND_CONFIG.rotate,
                base.bRot[i].y,
                base.bRot[i].z
            )

            const s = base.bScale[i]
            dummy.scale.set(s, s, s * (1 + WIND_CONFIG.stretch * lift))

            dummy.updateMatrix()
            leavesRef.current.setMatrixAt(i, dummy.matrix)
        }

        leavesRef.current.instanceMatrix.needsUpdate = true
    })

    return (
        <group position={position} rotation={rotation} scale={scale}>
            {/* Leaves (Instanced) */}
            <instancedMesh
                ref={leavesRef}
                args={[nodes.leaf_instance.geometry, material, count]}
            />

            {/* Trunk/Branches */}
            <mesh
                geometry={nodes.tree.geometry}
                material={material}
                position={[-0.05, 0.0, 0.403]}
            />
        </group>
    )
}
